import { endOfDay, formatDate, isSameDay, startOfDay } from "lopermut"
import { CalendarIcon, ChevronDownIcon } from "lucide-react"
import { DateRange } from "react-day-picker"
import { Button } from "../atoms/Button"
import { Calendar } from "../atoms/Calendar"
import { Input, InputProps } from "../atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/Popover"
import { cn } from "../utils"

type DatePickerProps = {
  date?: Date
  onDateChange: (date?: Date) => void
  placeholder?: string
  fromDate?: Date
  disabled?: boolean
}

function DatePicker({
  date,
  onDateChange,
  placeholder = "Select date",
  fromDate,
  disabled,
}: DatePickerProps) {
  return (
    <div className="flex flex-col gap-4">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id="date"
            className="h-10 font-normal gap-4"
            disabled={disabled}
          >
            <CalendarIcon />
            <div className="flex flex-row gap-2 w-full justify-between">
              {date ? formatDate(date) : placeholder}
              <ChevronDownIcon />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            captionLayout="dropdown"
            disabled={fromDate ? { before: fromDate } : undefined}
            onSelect={(date) => {
              onDateChange(date)
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

type TimePickerProps = Omit<InputProps, "type" | "value" | "onChange"> & {
  time: string
  onTimeChange: (time: string) => void
}

function TimePicker({
  time,
  onTimeChange,
  className,
  wrapperClassName,
  ...props
}: TimePickerProps) {
  return (
    <Input
      type="time"
      value={time}
      onChange={(event) => onTimeChange(event.target.value)}
      wrapperClassName={cn("w-auto", wrapperClassName)}
      className={cn(
        "h-10 w-28 appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
        className,
      )}
      {...props}
    />
  )
}

type DateRangeProps = {
  dateRange?: DateRange
  setDateRange: (dateRange?: DateRange) => void
  placeholder: string
  className?: HTMLDivElement["className"]
  fromDate?: Date
  maxRangeDays?: number
}

function addDays(date: Date, days: number) {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function DateRangePicker({
  dateRange,
  setDateRange,
  placeholder,
  className,
  fromDate,
  maxRangeDays,
}: DateRangeProps) {
  const renderDate = () => {
    if (dateRange?.from) {
      const start = formatDate(dateRange.from)

      if (dateRange?.to && !isSameDay(dateRange.from, dateRange.to)) {
        const end = formatDate(dateRange.to)
        return `${start} - ${end}`
      }

      return start
    }

    return <span>{placeholder}</span>
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn("h-10 justify-start text-left", !dateRange && "text-primary")}
          >
            <CalendarIcon className="h-4 w-4" />
            {renderDate()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            disabled={fromDate ? { before: fromDate } : undefined}
            onSelect={(dateRange) => {
              if (!dateRange) {
                setDateRange(undefined)
                return
              }

              const from = dateRange.from
                ? startOfDay(
                    dateRange.from < (fromDate ?? dateRange.from)
                      ? fromDate ?? dateRange.from
                      : dateRange.from,
                  )
                : undefined
              const maxTo = from && maxRangeDays ? endOfDay(addDays(from, maxRangeDays)) : undefined
              const selectedTo = dateRange.to ? endOfDay(dateRange.to) : undefined
              const to = selectedTo && maxTo && selectedTo > maxTo ? maxTo : selectedTo

              setDateRange({ from, to })
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export { DatePicker, DateRangePicker, TimePicker, type DateRange }
